import React, { useState, useEffect, useRef, useCallback } from 'react';

const CustomCursor = () => {
  // Current position of the cursor dot
  const [dotPosition, setDotPosition] = useState({ x: 0, y: 0 });
  // Current position of the cursor outline (which will lag slightly)
  const [outlinePosition, setOutlinePosition] = useState({ x: 0, y: 0 });
  // State for hover effect
  const [isHovering, setIsHovering] = useState(false);

  // Use refs to store the latest mouse position, separate from state for animation frame
  const mouse = useRef({ x: 0, y: 0 });
  // Ref for the animation frame ID
  const animationFrameId = useRef(null);

  // This function updates the cursor positions smoothly
  const animateCursor = useCallback(() => {
    // Smoothly interpolate outline position towards current mouse position
    setOutlinePosition((prevOutline) => ({
      x: prevOutline.x + (mouse.current.x - prevOutline.x) * 0.15, // 15% interpolation per frame
      y: prevOutline.y + (mouse.current.y - prevOutline.y) * 0.15,
    }));

    // Dot position updates instantly to the current mouse position
    setDotPosition({ x: mouse.current.x, y: mouse.current.y });

    // Request the next animation frame
    animationFrameId.current = requestAnimationFrame(animateCursor);
  }, []);

  useEffect(() => {
    // Event handler for mouse movement
    const onMouseMove = (e) => {
      mouse.current = { x: e.clientX, y: e.clientY }; // Update ref instantly

      // If animation is not running, start it
      if (!animationFrameId.current) {
        animationFrameId.current = requestAnimationFrame(animateCursor);
      }
    };

    // Event handlers for hover effects
    const onMouseOver = (e) => {
      if (e.target.closest('a, button, [data-hover]')) {
        setIsHovering(true);
      }
    };

    const onMouseOut = (e) => {
      if (!e.target.closest('a, button, [data-hover]')) {
        setIsHovering(false);
      }
    };

    // Attach event listeners
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseover', onMouseOver);
    document.addEventListener('mouseout', onMouseOut);

    // Initial start of animation frame (in case mouse doesn't move immediately)
    animationFrameId.current = requestAnimationFrame(animateCursor);

    // Cleanup function
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseover', onMouseOver);
      document.removeEventListener('mouseout', onMouseOut);

      // Cancel the animation frame on component unmount
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [animateCursor]); // Dependency on useCallback `animateCursor`

  return (
    <>
      <div
        className={`cursor-outline ${isHovering ? 'hovering' : ''}`}
        style={{ left: `${outlinePosition.x}px`, top: `${outlinePosition.y}px` }}
      />
      <div
        className={`cursor-dot ${isHovering ? 'hovering' : ''}`}
        style={{ left: `${dotPosition.x}px`, top: `${dotPosition.y}px` }}
      />
    </>
  );
};

export default CustomCursor;